/**
 * `eventIcon` — the one icon + colour vocabulary for the per-task timeline.
 *
 * Fourteen simple line-glyphs (one per `TaskEventType`), ported from the
 * Claude-design wireframe bundle (2026-05-25) and recoloured against the app's
 * own palette + `TASK_STATES` so a "Done" dot looks identical to a "Done" chip
 * everywhere (not the wireframe's standalone teal). Geometric + calm — the rail
 * must never read as confetti, so only the "louder" events (created / assigned
 * / state change / dependency linked) carry the brand teal; everything else
 * sits quiet ink-muted, and a `state_changed` event borrows its destination
 * state's colour.
 *
 * Pure + presentational: no data fetching, no scheduler import (`taskmap ⊥
 * scheduler`). The phrasing of an event lives in `describeEvent`; the glyph +
 * accent live here; the two are kept in lock-step by `TaskEventType`.
 *
 * Brand firewall (§A9): an icon describes WHAT happened to the task. Nothing
 * here aggregates or characterises a person.
 */

import type { ReactNode } from "react";

import type { MapTheme } from "../theme/tokens";
import { PALETTE } from "../tree/palette";
import { type TaskState, taskStateStyle } from "../tree/state";
import type { TaskEventType } from "./describeEvent";

// ── The 14 glyphs (18×18 viewBox, 1.6 stroke = currentColor) ─────────────────

const GLYPHS: Record<TaskEventType, ReactNode> = {
  // Task created — a spark inside a soft circle.
  node_created: (
    <>
      <circle cx="9" cy="9" r="6.5" />
      <path d="M9 6v6M6 9h6" />
    </>
  ),
  // Renamed — a pencil over a baseline.
  node_renamed: (
    <>
      <path d="M4 13.5l1-3 6-6 2 2-6 6-3 1z" />
      <path d="M3.5 15.5h11" />
    </>
  ),
  // Description edited — a pencil over stacked lines.
  node_redescribed: (
    <>
      <path d="M3.5 5.5h7M3.5 9h9M3.5 12.5h5" />
      <path d="M12 11l2.5-2.5 1.5 1.5L13.5 12.5l-2 .5z" />
    </>
  ),
  // Re-parented — an arrow swinging between two nodes.
  node_reparented: (
    <>
      <circle cx="4" cy="5" r="1.5" />
      <circle cx="14" cy="13" r="1.5" />
      <path d="M4 6.5v3a3 3 0 0 0 3 3h5" />
      <path d="M10.5 11l1.5 1.5-1.5 1.5" />
    </>
  ),
  // Re-ordered — up + down arrows.
  node_reordered: (
    <>
      <path d="M6 4v10M6 14l-2-2M6 14l2-2" />
      <path d="M12 14V4M12 4l-2 2M12 4l2 2" />
    </>
  ),
  // Person assigned — head/shoulders + plus.
  node_assigned: (
    <>
      <circle cx="8" cy="7" r="2.2" />
      <path d="M4 14c.5-2 2-3 4-3s3.5 1 4 3" />
      <path d="M14 4v3M12.5 5.5h3" />
    </>
  ),
  // Person unassigned — head/shoulders + minus.
  node_unassigned: (
    <>
      <circle cx="8" cy="7" r="2.2" />
      <path d="M4 14c.5-2 2-3 4-3s3.5 1 4 3" />
      <path d="M12.5 5.5h3" />
    </>
  ),
  // State changed — a half-filled circle (the accent carries the new state).
  state_changed: (
    <>
      <circle cx="9" cy="9" r="6" />
      <path d="M9 3a6 6 0 0 1 0 12z" fill="currentColor" stroke="none" />
    </>
  ),
  // Dependency linked — two interlocked rings.
  dependency_added: (
    <>
      <rect x="2" y="6.5" width="7" height="5" rx="2.5" />
      <rect x="9" y="6.5" width="7" height="5" rx="2.5" />
    </>
  ),
  // Dependency unlinked — the rings broken apart.
  dependency_removed: (
    <>
      <path d="M7 7.5h-1.5a2.5 2.5 0 0 0 0 5H7" />
      <path d="M11 7.5h1.5a2.5 2.5 0 0 1 0 5H11" />
      <path d="M4 4l10 10" />
    </>
  ),
  // Note added — a page with a plus.
  note_added: (
    <>
      <path d="M4 3.5h7l3 3v8H4z" />
      <path d="M11 3.5v3h3" />
      <path d="M9 9v4M7 11h4" />
    </>
  ),
  // Note removed — a page with a minus.
  note_removed: (
    <>
      <path d="M4 3.5h7l3 3v8H4z" />
      <path d="M11 3.5v3h3" />
      <path d="M7 11h4" />
    </>
  ),
  // Archived — an open box with a slot.
  node_archived: (
    <>
      <path d="M2.5 5.5h13v2.5h-13z" />
      <path d="M3.5 8v6.5h11V8" />
      <path d="M7 11h4" />
    </>
  ),
  // Target date set / cleared — a clock face (the `◷` glyph the live node
  // carries). Provenance, not an alarm; it stays quiet ink-muted on the rail.
  target_date_set: (
    <>
      <circle cx="9" cy="9" r="6.5" />
      <path d="M9 5v4l2.5 2" />
    </>
  ),
};

// A calm fallback for an unrecognised (future) event type — a small dot.
const FALLBACK_GLYPH: ReactNode = (
  <circle cx="9" cy="9" r="2.5" fill="currentColor" stroke="none" />
);

/**
 * The glyph for an event type, as an inline SVG. `currentColor` drives the
 * stroke, so the caller colours it by setting `color` on a wrapper. An unknown
 * type degrades to a quiet dot (the log is additive-only).
 */
export function EventIcon({
  type,
  size = 14,
}: {
  type: TaskEventType | string;
  size?: number;
}) {
  const glyph = GLYPHS[type as TaskEventType] ?? FALLBACK_GLYPH;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      {glyph}
    </svg>
  );
}

// ── Accent colours ───────────────────────────────────────────────────────────

/** The "louder" events that carry the brand teal (everything else stays quiet). */
const TEAL_EVENTS = new Set<TaskEventType>([
  "node_created",
  "node_assigned",
  "dependency_added",
  "state_changed", // overridden by the destination state colour when known
]);

/** A soft teal + soft neutral, calm tints for the rail dots. */
const TEAL_SOFT = "#E4F1EE";
const QUIET_SOFT = "#EEF3F1";

export interface EventVisual {
  /** Foreground (icon + dot border) colour. */
  readonly fg: string;
  /** Soft dot background. */
  readonly bg: string;
}

/**
 * The dot colour for an event. A `state_changed` event with a known
 * destination borrows that state's colour (so "→ Done" reads green, "→ Blocked"
 * calm amber); the other "louder" events take brand teal; everything else stays
 * quiet ink-muted. Keeps the rail legible, not noisy.
 *
 * Editorial re-skin (#1043, Wave 2): pass the resolved `theme` and the dot
 * recolours editorially — a `state_changed` event uses `theme.stateStyle`'s
 * darker chip-text `fg` + warm `chipBg`, "louder" events take the editorial
 * `--teal` over a soft `--teal-soft` wash, and the quiet events take editorial
 * `--ink-3` over `--bg-2`. Omit `theme` (or pass a legacy theme) and the result
 * is BYTE-IDENTICAL to the shipped legacy colours — the static `taskStateStyle`
 * / `PALETTE` constants, unchanged.
 */
export function eventVisual(
  eventType: TaskEventType | string,
  toState: TaskState | null,
  theme?: MapTheme,
): EventVisual {
  const editorial = theme?.mode === "editorial";
  if (eventType === "state_changed" && toState) {
    const s = editorial && theme ? theme.stateStyle(toState) : taskStateStyle(toState);
    return { fg: s.fg, bg: s.chipBg };
  }
  if (TEAL_EVENTS.has(eventType as TaskEventType)) {
    return editorial && theme
      ? { fg: theme.palette.teal, bg: theme.palette.tealSoft }
      : { fg: PALETTE.teal, bg: TEAL_SOFT };
  }
  return editorial && theme
    ? { fg: theme.palette.muted, bg: theme.palette.bgRecess }
    : { fg: PALETTE.muted, bg: QUIET_SOFT };
}
